import { useEffect, useRef } from 'react';
import type { PlayerAction } from '../playerReducer';

/**
 * Polls `mediaEl.currentTime` via `requestAnimationFrame` during playback and
 * dispatches `TIME_UPDATE` every time the video frame changes.  This gives
 * smooth, frame-accurate timecode display instead of the browser's ~250ms
 * `timeupdate` event cadence.
 */
export function useFrameAccurateTime(
  mediaEl: HTMLMediaElement | null,
  isPlaying: boolean,
  fps: number | undefined,
  dispatch: React.Dispatch<PlayerAction>,
) {
  const lastFrameRef = useRef(-1);

  useEffect(() => {
    if (!mediaEl || !isPlaying) {
      lastFrameRef.current = -1;
      return;
    }

    let animId: number;
    const effectiveFps = fps || 30;

    const tick = () => {
      const time = mediaEl.currentTime;
      const frame = Math.floor(time * effectiveFps);
      if (frame !== lastFrameRef.current) {
        lastFrameRef.current = frame;
        dispatch({ type: 'TIME_UPDATE', currentTime: time });
      }
      animId = requestAnimationFrame(tick);
    };

    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, [mediaEl, isPlaying, fps, dispatch]);
}
